import { useEffect, useState } from 'react';
import { t } from 'utilities/TranslationUtils';
import { observer } from 'mobx-react-lite';

// components
import { AppSpacer, AppUseIcon, AppCard, AppLightTooltip } from 'components/ui';
import { AppButton, AppInput } from 'components/form';
import { Loader } from '@trackman/web-shared-components';

// types
import { useStores } from 'index';
import { EmailShouldBeValid } from 'utilities/Validations';
import { UserData } from 'types/models/UserData';

export const SelectUserStep = observer(
  ({ selectedUser, onSelectUser }: { selectedUser: UserData | null; onSelectUser: (user: UserData) => void }) => {
    const { usersStore, apiServiceAxiosInstance: apiInstance } = useStores();
    const [email, setEmail] = useState('');
    const [searchResult, setSearchResult] = useState<UserData[] | null>([]);
    const [isSearchCompleted, setIsSearchCompleted] = useState(false);

    useEffect(() => {
      if (!selectedUser) {
        setEmail('');
        setSearchResult([]);
        return;
      }
      if (selectedUser.email === email) return;
      setEmail(selectedUser.email);
      setSearchResult([selectedUser]);
    }, [selectedUser]);

    const onSearchHandler = async () => {
      if (usersStore.isSearchingForNewUser || !email) return;
      setIsSearchCompleted(false);
      const res = await usersStore.findNewUserByEmail(apiInstance, email);
      setSearchResult(res);
      setIsSearchCompleted(true);
    };

    const isUserFound = () => searchResult && searchResult.length > 0;

    return (
      <>
        <SearchForUserComponent
          usersStore={usersStore}
          email={email}
          setEmail={setEmail}
          onSearchHandler={onSearchHandler}
          setIsSearchCompleted={setIsSearchCompleted}
        />

        <AppSpacer space='30px' />
        {isUserFound() ? (
          <UserFoundComponent onSelectUser={onSelectUser} searchResult={searchResult} />
        ) : !usersStore.isSearchingForNewUser && isSearchCompleted && email && searchResult && searchResult.length === 0 ? (
          <AddNewUserComponent user={{ email }} onSelectUser={onSelectUser} />
        ) : null}
      </>
    );
  }
);

const AddNewUserComponent = ({ user, onSelectUser }: any) => {
  return (
    <section>
      <SingleUserComponent user={user} onSelectUser={onSelectUser} />
    </section>
  );
};

const SingleUserComponent = ({ user, onSelectUser }: any) => {
  const isUserFound = user && user.id;
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);

  return (
    <AppCard className='p-3'>
      <div className='row'>
        <div className='col-2'>
          <img src={user.profilePhotoUrl} height={'100px'} className='rounded-circle w-100 bg-secondary aspect-ratio-1-1' alt='' />
        </div>
        <div className='col-10 d-flex flex-column position-relative'>
          <div>
            {isUserFound && (
              <h3 className='font-18 mb-0'>
                {user.firstName} {user.lastName}
              </h3>
            )}
            {user.email && (
              <p className='mb-0'>
                <a href={`mailto:${user.email}`} className=' mb-0 color-textElementGrey text-underline'>
                  {user.email}
                  <AppUseIcon name='ExternalLinkIcon' height='11' width='11' className='ml-2' />
                </a>
              </p>
            )}
            {user.phoneNumber && (
              <p className='mb-0'>
                <a href={`tel:${user.phoneNumber}`} className=' mb-0 color-textElementGrey text-underline'>
                  {user.phoneNumber}
                  <AppUseIcon name='ExternalLinkIcon' height='11' width='11' className='ml-2' />
                </a>
              </p>
            )}
            <p className='mb-0'>
              {user.organizationName} {user.organizationShortName ? `(${user.organizationShortName})` : ''}
            </p>

            {user.emailConfirmed ? (
              <p className='mb-0 d-flex align-items-center'>
                <AppUseIcon className='mr-2' width='14' height='14' name='SuccessCircleIcon' />
                {t('usersPage.emailConfirmed')}
              </p>
            ) : (
              <p className='mb-0 d-flex align-items-center'>
                <AppUseIcon className='mr-2' width='14' height='14' name='WarningCircleIcon' />
                {t('usersPage.emailNotConfirmed')}
              </p>
            )}

            {!isUserFound && (
              <div className='d-flex flex-row my-3'>
                <AppInput
                  label={t('usersPage.firstName')}
                  name={'firstName'}
                  value={firstName}
                  onChange={(e: any) => setFirstName(e.target.value)}
                />
                <AppSpacer isHorizontal space='1rem' />
                <AppInput
                  label={t('usersPage.lastName')}
                  name={'lastName'}
                  value={lastName}
                  onChange={(e: any) => setLastName(e.target.value)}
                />
              </div>
            )}
            <AppSpacer isHorizontal space='40px' />
          </div>
          <div className='position-absolute bottom-0 right-1rem d-flex'>
            <AppButton
              variant='primary'
              onClick={() =>
                onSelectUser({
                  ...user,
                  firstName: firstName || user.firstName,
                  lastName: lastName || user.lastName,
                })
              }
              className='d-flex align-items-center '
              disabled={!isUserFound && (!firstName?.trim() || !lastName?.trim())}
            >
              <AppUseIcon name='SearchIcon' width='16' height='16' className='mr-2' color={'currentColor'} />
              {t('usersPage.selectThisUser')}
            </AppButton>
          </div>
        </div>
      </div>
    </AppCard>
  );
};

const SearchForUserComponent = ({
  usersStore,
  email,
  setEmail,
  onSearchHandler,
  setIsSearchCompleted,
}: {
  usersStore: any;
  email: string;
  setEmail: any;
  onSearchHandler: any;
  setIsSearchCompleted: (value: boolean) => void;
}) => {
  return (
    <section className='d-flex align-items-end justify-content-center'>
      <AppInput
        name='email'
        label={t('usersPage.email')}
        value={email}
        disabled={usersStore.isSearchingForNewUser}
        validationRules={[
          EmailShouldBeValid,
          (value: string) => usersStore.users.some((user) => user.email === value) && 'USER_ALREADY_EXIST',
        ]}
        onChange={(e) => {
          setIsSearchCompleted(false);
          setEmail(e.target.value);
        }}
        onKeyDown={(e) => e.key === 'Enter' && onSearchHandler()}
        data-test='user-dialog-email-input'
      />

      <AppButton
        variant={usersStore.isSearchingForNewUser ? 'white' : 'primary'}
        onClick={() => onSearchHandler()}
        disabled={usersStore.isSearchingForNewUser || !email}
        className='ml-2'
        width='150px'
        data-test='user-dialog-search-button'
      >
        {usersStore.isSearchingForNewUser}
        {usersStore.isSearchingForNewUser ? (
          <Loader size='small' />
        ) : (
          <>
            <AppUseIcon name='SearchIcon' color='currentColor' width='19' height='19' className=' mr-1' />
            {t('usersPage.search')}
          </>
        )}
      </AppButton>
    </section>
  );
};

const UserFoundComponent = ({ onSelectUser, searchResult }: any) => {
  return (
    <section>
      {searchResult.map((user) => {
        return (
          <div className='find-user-form' key={user.id}>
            <SingleUserComponent user={user} onSelectUser={onSelectUser} />
          </div>
        );
      })}
    </section>
  );
};

export default SelectUserStep;
